import Color from 'colorjs.io';
import { colorEngine, CmykProfilePlugin, convertColorToCmyk } from '../colorEngine';
import type { ICCProfileDescriptor, ICCProfileSnapshot, RenderingIntent } from './types';

interface InternalProfile {
  descriptor: ICCProfileDescriptor;
  buffer: ArrayBuffer;
}

type Listener = (snapshot: ICCProfileSnapshot) => void;

const MAX_PROFILE_SIZE_BYTES = 10 * 1024 * 1024;
const MAX_PROFILES = 5;

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function ensureId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function intentLabel(intent: RenderingIntent) {
  switch (intent) {
    case 'perceptual':
      return 'Perceptual';
    case 'relative':
      return 'Relative Colorimetric';
    case 'absolute':
      return 'Absolute Colorimetric';
    case 'saturation':
      return 'Saturation';
    default:
      return intent;
  }
}

function simulateIccTransform(color: Color, descriptor: ICCProfileDescriptor) {
  let [c, m, y, k] = convertColorToCmyk(color);

  const modifierBase = ((descriptor.fileName.length % 7) + 3) / 10;

  const applyIntent = (intent: RenderingIntent) => {
    switch (intent) {
      case 'perceptual': {
        const lift = modifierBase * 0.05;
        c = clamp(c - lift * 0.4, 0, 1);
        m = clamp(m - lift * 0.4, 0, 1);
        y = clamp(y - lift * 0.4, 0, 1);
        k = clamp(k - lift * 0.25, 0, 1);
        break;
      }
      case 'relative': {
        const tweak = modifierBase * 0.03;
        c = clamp(c - tweak * 0.2, 0, 1);
        m = clamp(m - tweak * 0.15, 0, 1);
        y = clamp(y - tweak * 0.1, 0, 1);
        k = clamp(k, 0, 1);
        break;
      }
      case 'absolute': {
        const boost = modifierBase * 0.04;
        k = clamp(k + boost * 0.3, 0, 1);
        c = clamp(c + boost * 0.1, 0, 1);
        m = clamp(m + boost * 0.1, 0, 1);
        y = clamp(y + boost * 0.05, 0, 1);
        break;
      }
      case 'saturation': {
        const satBoost = modifierBase * 0.07;
        const adjust = (value: number, factor: number) => clamp(value - satBoost * factor, 0, 1);
        c = adjust(c, 0.45);
        m = adjust(m, 0.35);
        y = adjust(y, 0.25);
        k = clamp(k - satBoost * 0.1, 0, 1);
        break;
      }
      default:
        break;
    }
  };

  applyIntent(descriptor.renderingIntent);

  if (descriptor.blackPointCompensation) {
    const compensation = 0.08 + modifierBase * 0.05;
    k = clamp(k - compensation, 0, 1);
  }

  return {
    c,
    m,
    y,
    k,
  };
}

class ICCManager {
  private profiles = new Map<string, InternalProfile>();
  private listeners = new Set<Listener>();
  private activeProfileId: string | null = null;
  private status: ICCProfileSnapshot['status'] = 'idle';
  private error: string | null = null;
  private revision = 0;

  getSnapshot(): ICCProfileSnapshot {
    return {
      profiles: Array.from(this.profiles.values()).map((entry) => entry.descriptor),
      activeProfileId: this.activeProfileId,
      status: this.status,
      error: this.error,
      revision: this.revision,
    };
  }

  subscribe(listener: Listener) {
    this.listeners.add(listener);
    listener(this.getSnapshot());
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    const snapshot = this.getSnapshot();
    for (const listener of this.listeners) {
      listener(snapshot);
    }
  }

  private incrementRevision() {
    this.revision += 1;
  }

  private buildPlugin(descriptor: ICCProfileDescriptor): CmykProfilePlugin {
    return {
      id: descriptor.pluginId,
      label: `${descriptor.name} (${intentLabel(descriptor.renderingIntent)})`,
      transform: (color) => simulateIccTransform(color, descriptor),
    };
  }

  private registerPlugin(descriptor: ICCProfileDescriptor) {
    const plugin = this.buildPlugin(descriptor);
    colorEngine.registerCmykProfilePlugin(plugin);
  }

  private hasPlugin(pluginId: string) {
    for (const profile of this.profiles.values()) {
      if (profile.descriptor.pluginId === pluginId) {
        return true;
      }
    }
    return false;
  }

  async uploadProfile(
    file: File,
    options: {
      renderingIntent: RenderingIntent;
      blackPointCompensation: boolean;
    },
  ) {
    if (this.profiles.size >= MAX_PROFILES) {
      const message = `You can keep up to ${MAX_PROFILES} ICC profiles loaded at once. Remove one before adding another.`;
      this.error = message;
      this.notify();
      throw new Error(message);
    }

    if (!/\.(icc|icm)$/i.test(file.name)) {
      const message = 'Only .icc and .icm files are supported.';
      this.error = message;
      this.notify();
      throw new Error(message);
    }

    if (file.size > MAX_PROFILE_SIZE_BYTES) {
      const message = 'ICC profile exceeds the 10MB size limit.';
      this.error = message;
      this.notify();
      throw new Error(message);
    }

    this.status = 'loading';
    this.error = null;
    this.notify();

    try {
      const buffer = await file.arrayBuffer();
      const id = ensureId();
      const pluginId = `icc:${id}`;

      const descriptor: ICCProfileDescriptor = {
        id,
        name: file.name.replace(/\.(icc|icm)$/i, ''),
        fileName: file.name,
        size: file.size,
        createdAt: Date.now(),
        renderingIntent: options.renderingIntent,
        blackPointCompensation: options.blackPointCompensation,
        pluginId,
      };

      this.profiles.set(id, { descriptor, buffer });
      this.registerPlugin(descriptor);

      this.activeProfileId = pluginId;
      this.status = 'idle';
      this.error = null;
      this.incrementRevision();
      this.notify();

      return descriptor;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to load ICC profile.';
      this.status = 'idle';
      this.error = message;
      this.notify();
      throw new Error(message);
    }
  }

  removeProfile(id: string) {
    const profile = this.profiles.get(id);
    if (!profile) {
      return;
    }

    colorEngine.unregisterCmykProfilePlugin(profile.descriptor.pluginId);
    this.profiles.delete(id);

    if (this.activeProfileId === profile.descriptor.pluginId) {
      this.activeProfileId = null;
    }

    this.incrementRevision();
    this.notify();
  }

  setActiveProfile(id: string | null) {
    if (id !== null && !this.hasPlugin(id)) {
      this.activeProfileId = null;
    } else {
      this.activeProfileId = id;
    }
    this.incrementRevision();
    this.notify();
  }

  updateProfile(
    id: string,
    updates: Partial<Pick<ICCProfileDescriptor, 'renderingIntent' | 'blackPointCompensation' | 'notes' | 'name'>>,
  ) {
    const profile = this.profiles.get(id);
    if (!profile) {
      throw new Error('Profile not found');
    }

    const updatedDescriptor: ICCProfileDescriptor = {
      ...profile.descriptor,
      ...updates,
    };

    this.profiles.set(id, { ...profile, descriptor: updatedDescriptor });
    this.registerPlugin(updatedDescriptor);
    this.incrementRevision();
    this.notify();
    return updatedDescriptor;
  }
}

export const iccManager = new ICCManager();
